/**
 * Local preferences with a versioned schema. Reads are validated field-by-field so a corrupt or
 * partial record degrades to safe defaults instead of failing; unavailable storage (private
 * modes, blocked cookies) falls back to in-memory preferences for the session.
 */
import { z } from 'zod';
import { AUTO_GRAPHICS, type GraphicsOverrides } from '../framework/quality/profiles';

export const PREFS_VERSION = 1;
const STORAGE_KEY = 'gbr.prefs';

const stage = z.enum(['dawn', 'morning', 'afternoon', 'golden', 'dusk', 'night']);

/** Hand-set graphics options: anything missing or unknown falls back to automatic. */
const graphics = z.object({
  resolution: z.enum(['auto', '50', '75', '100', '125', '150', '200']).catch('auto'),
  frameRate: z.enum(['auto', '30', '60', 'max']).catch('auto'),
  shadows: z.enum(['auto', 'low', 'medium', 'high', 'ultra']).catch('auto'),
  lightShafts: z.enum(['auto', 'off', 'low', 'medium', 'high']).catch('auto'),
  ambientOcclusion: z.enum(['auto', 'off', 'on']).catch('auto'),
  depthOfField: z.enum(['auto', 'off', 'subtle', 'full']).catch('auto'),
  bloom: z.enum(['auto', 'off', 'on']).catch('auto'),
  smoothing: z.enum(['auto', 'off', 'on']).catch('auto'),
  furMoss: z.enum(['auto', 'low', 'medium', 'high', 'ultra']).catch('auto'),
}) satisfies z.ZodType<GraphicsOverrides>;

export const PrefsSchema = z.object({
  version: z.literal(PREFS_VERSION),
  audio: z.object({
    musicVolume: z.number().min(0).max(1),
    envVolume: z.number().min(0).max(1),
    musicMuted: z.boolean(),
    envMuted: z.boolean(),
    ambientBreaks: z.boolean(),
    shuffle: z.boolean(),
    repeat: z.enum(['off', 'all', 'one']),
    keepMusicOnWorldChange: z.boolean(),
    spatial: z.boolean(),
    sleepMusicOnly: z.boolean(),
    /** Keep sound playing while the tab is hidden. */
    playWhenHidden: z.boolean(),
  }),
  visual: z.object({
    preference: z.enum(['battery', 'balanced', 'high']),
    detail: z.enum(['auto', 'low', 'medium', 'high', 'ultra']),
    density: z.enum(['low', 'normal', 'lush']),
    autoPerformance: z.boolean(),
    /** Advanced graphics options pinned by hand (all automatic by default). */
    graphics,
    /** Background wildlife (deer, birds, the owl) and falling leaves. */
    wildlife: z.boolean(),
    fallingLeaves: z.boolean(),
    /** A small frame-rate readout over the world. */
    showFrameRate: z.boolean(),
  }),
  time: z.object({
    mode: z.enum(['living', 'local', 'hold']),
    heldHour: z.number().min(0).max(24).optional(),
    heldStage: stage.optional(),
  }),
  weather: z.object({ mood: z.enum(['auto', 'clear', 'overcast', 'rain']) }),
  a11y: z.object({ reducedMotion: z.enum(['system', 'on', 'off']) }),
  lastWorld: z.string().optional(),
});

export type Prefs = z.infer<typeof PrefsSchema>;

export const DEFAULT_PREFS: Prefs = {
  version: PREFS_VERSION,
  audio: {
    musicVolume: 0.6,
    envVolume: 0.7,
    musicMuted: false,
    envMuted: false,
    ambientBreaks: true,
    shuffle: false,
    repeat: 'all',
    keepMusicOnWorldChange: false,
    spatial: true,
    sleepMusicOnly: false,
    playWhenHidden: true,
  },
  visual: { preference: 'balanced', detail: 'auto', density: 'normal', autoPerformance: true, graphics: { ...AUTO_GRAPHICS }, wildlife: true, fallingLeaves: true, showFrameRate: false },
  time: { mode: 'living' },
  weather: { mood: 'auto' },
  a11y: { reducedMotion: 'system' },
};

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function safeStorage(): StorageLike | null {
  try {
    const s = window.localStorage;
    const probe = '__gbr_probe__';
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}

/** Merge an unknown stored value onto defaults, keeping only fields that validate. */
export function recoverPrefs(raw: unknown): Prefs {
  const out: Prefs = structuredClone(DEFAULT_PREFS);
  if (!raw || typeof raw !== 'object') return out;
  const r = raw as Record<string, unknown>;
  // Future versions migrate here (e.g. if (r.version === 1) …). Unknown versions → defaults
  // for any section that no longer validates.
  const sections = ['audio', 'visual', 'time', 'weather', 'a11y'] as const;
  for (const key of sections) {
    const src = r[key];
    if (!src || typeof src !== 'object') continue;
    const shape = PrefsSchema.shape[key];
    const merged = { ...(out[key] as object), ...(src as object) };
    const parsed = shape.safeParse(merged);
    if (parsed.success) {
      (out as Record<string, unknown>)[key] = parsed.data;
    } else {
      // Field-by-field recovery: keep valid fields, reset invalid ones.
      const fields = (shape as z.ZodObject<z.ZodRawShape>).shape;
      const partial: Record<string, unknown> = { ...(out[key] as object) };
      for (const [f, schema] of Object.entries(fields)) {
        const v = (src as Record<string, unknown>)[f];
        if (v !== undefined && (schema as z.ZodType).safeParse(v).success) partial[f] = v;
      }
      (out as Record<string, unknown>)[key] = partial;
    }
  }
  if (typeof r.lastWorld === 'string') out.lastWorld = r.lastWorld;
  return out;
}

type Listener = (p: Prefs) => void;

export class PrefsStore {
  private value: Prefs;
  private listeners = new Set<Listener>();
  private storage: StorageLike | null;
  readonly persistent: boolean;

  constructor(storage: StorageLike | null = typeof window !== 'undefined' ? safeStorage() : null) {
    this.storage = storage;
    this.persistent = storage !== null;
    let raw: unknown;
    try {
      const text = storage?.getItem(STORAGE_KEY);
      raw = text ? JSON.parse(text) : null;
    } catch {
      raw = null;
    }
    this.value = recoverPrefs(raw);
  }

  get(): Prefs {
    return this.value;
  }

  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  update(mutator: (draft: Prefs) => void): void {
    const next = structuredClone(this.value);
    mutator(next);
    const parsed = PrefsSchema.safeParse(next);
    if (!parsed.success) return;
    this.value = parsed.data;
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify(this.value));
    } catch {
      /* quota or privacy mode: keep in memory */
    }
    this.listeners.forEach((fn) => fn(this.value));
  }
}
